import type * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Collapsible } from '../../../components/Collapsible';
import { AdvancedOptionsButton } from '../../../components/automation/AutomationStepCard/presentation';
import { StepSendFromOption } from '../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { ConditionalFilter } from './ConditionalFilter';
import type { IActionItemAutomationStepExtended } from '../types/automationSteps';

/**
 * ActionItemAdvancedOptions component.
 *
 * Displays collapsible advanced options for action item steps including:
 * - Send from options (assignment)
 * - Conditional contact filters (skip criteria)
 *
 * @param actionItemStep - The action item step data
 * @param nodeId - The node identifier
 * @param stepId - The step identifier
 * @param skipStepContactFilter - Current contact filter criteria
 * @param canEdit - Whether the step can be edited
 * @param isCollapsed - Whether the advanced options are collapsed
 * @param onToggle - Handler for toggling collapsed state
 * @param onSaveSenderOptions - Handler for saving sender options
 * @param onFilterChange - Handler for filter changes
 */
interface ActionItemAdvancedOptionsProps {
	actionItemStep: IActionItemAutomationStepExtended | null;
	nodeId: string;
	stepId: string;
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	canEdit: boolean;
	isCollapsed: boolean;
	onToggle: () => void;
	onSaveSenderOptions: (option: Api.ISendFromOptions) => void;
	onFilterChange: (filter: Api.IContactsFilterRequest | null) => void;
}

export function ActionItemAdvancedOptions({
	actionItemStep,
	nodeId,
	stepId,
	skipStepContactFilter,
	canEdit,
	isCollapsed,
	onToggle,
	onSaveSenderOptions,
	onFilterChange,
}: ActionItemAdvancedOptionsProps) {
	// StepSendFromOption expects a legacy AutomationStepViewModel structure.
	// Cast to unknown first to avoid direct incompatible type assertion.
	const stepWrapper: Api.AutomationTemplateEditorStep<Api.AutomationStepViewModel> | null = actionItemStep
		? {
				automationStep: actionItemStep as unknown as Api.AutomationStepViewModel,
				uuid: actionItemStep.id || nodeId,
			}
		: null;

	return (
		<>
			<AdvancedOptionsButton isCollapsed={isCollapsed} onClick={onToggle} />
			<Collapsible isCollapsed={isCollapsed}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					{stepWrapper && (
						<StepSendFromOption
							step={stepWrapper}
							onSave={onSaveSenderOptions}
							connectionTypes={[]}
							sendFromLabel={'Always assign this action item to'}
							canEdit={canEdit}
						/>
					)}
					<ConditionalFilter
						stepId={stepId}
						filter={skipStepContactFilter}
						canEdit={canEdit}
						isBusy={actionItemStep?.isBusy}
						onFilterChange={onFilterChange}
					/>
				</div>
			</Collapsible>
		</>
	);
}
